// Edits a customer's per-hub allocations: a list of (hub, percentage, effective
// date, until date) rows. Kept as its own component so CustomerForm stays
// scannable.
//
// Date-ranged: leave "until" blank for open-ended. To schedule a hub switch, set
// the old hub's "until" = switch date and add the new hub with "effective" = the
// same date. Values are raw strings; CustomerForm validates + converts on submit.

import { Button, Input, Select } from "../../components/ui";
import type { Hub } from "../../lib/types";

export interface AllocationDraft {
  hubId: string;
  percentage: string;
  effectiveDate: string;
  endDate: string;
}

export function emptyAllocation(): AllocationDraft {
  return { hubId: "", percentage: "", effectiveDate: "", endDate: "" };
}

interface Props {
  hubs: Hub[];
  value: AllocationDraft[];
  onChange: (next: AllocationDraft[]) => void;
}

export default function AllocationsEditor({ hubs, value, onChange }: Props) {
  function update(index: number, patch: Partial<AllocationDraft>) {
    onChange(value.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }
  function remove(index: number) {
    onChange(value.filter((_, i) => i !== index));
  }
  function add() {
    onChange([...value, emptyAllocation()]);
  }

  if (hubs.length === 0) {
    return (
      <p className="text-sm text-slate-500">
        No hubs yet — add a hub before allocating customer volume.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {value.length > 0 && (
        <div className="flex gap-2 px-1 text-xs font-medium text-slate-400">
          <div className="flex-1">Hub</div>
          <div className="w-16">%</div>
          <div className="w-36">Effective</div>
          <div className="w-36">Until</div>
          <div className="w-8" />
        </div>
      )}

      {value.map((row, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="flex-1">
            <Select value={row.hubId} onChange={(e) => update(i, { hubId: e.target.value })}>
              <option value="">Select hub…</option>
              {hubs.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.name}
                </option>
              ))}
            </Select>
          </div>
          <div className="w-16">
            <Input
              type="number"
              min={0}
              max={100}
              value={row.percentage}
              onChange={(e) => update(i, { percentage: e.target.value })}
              placeholder="%"
            />
          </div>
          <div className="w-36">
            <Input
              type="date"
              value={row.effectiveDate}
              onChange={(e) => update(i, { effectiveDate: e.target.value })}
              title="Effective date (optional)"
            />
          </div>
          <div className="w-36">
            <Input
              type="date"
              value={row.endDate}
              onChange={(e) => update(i, { endDate: e.target.value })}
              title="Until date (optional; blank = open-ended)"
            />
          </div>
          <Button type="button" variant="ghost" onClick={() => remove(i)} aria-label="Remove allocation">
            ✕
          </Button>
        </div>
      ))}

      <Button type="button" variant="ghost" onClick={add}>
        + Add allocation
      </Button>
    </div>
  );
}
